import {
  Navigate,
  RouterProvider,
  createRouter,
  parseSearchWith,
  type AnySchema,
  type AnyRoute,
  type AnyRouter,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";

import { ErrorComponent } from "../components/ErrorComponent/ErrorComponent";
import { PendingPage } from "../components/PendingPage/PendingPage";

import { createAppRoot, queryClient, type CreateRootOptions } from "./root";

type CreateRouterRootOptions = Omit<CreateRootOptions, "children"> & {
  routing: {
    routeTree: AnyRoute;
    defaultRoute: string;
  };
};

// We have a bug in Tanstack router that doesn't handle + signs in search params correctly.
// This function is an attempt to workaround.
const jsonSearchParser = parseSearchWith(JSON.parse);
function parseSearch(searchStr: string): AnySchema {
  return jsonSearchParser(searchStr.replace(/\+/g, "%20"));
}

export async function createRouterAppRoot({
  sentry,
  routing,
  ...options
}: CreateRouterRootOptions): Promise<void> {
  // Needed to avoid type errors
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const router = createRouter({
    defaultPreload: "intent",
    parseSearch,
    routeTree: routing.routeTree,
    notFoundMode: "root",
    defaultPendingMs: 0,
    defaultStaleTime: Infinity,
    defaultErrorComponent: ErrorComponent,
    defaultPendingComponent: PendingPage,
    defaultNotFoundComponent() {
      return <Navigate to={routing.defaultRoute} />;
    },
    context: { queryClient },
    basepath: typeof window._msBaseUrl === "string" ? window._msBaseUrl : "/",
  }) as AnyRouter;

  return createAppRoot({
    children: <RouterProvider router={router} />,
    devtoolsPlugins: [
      {
        name: "Router",
        render: <TanStackRouterDevtoolsPanel router={router} />,
      },
    ],
    sentry: sentry
      ? {
          ...sentry,
          router,
        }
      : undefined,
    ...options,
  });
}
